<template>
  <Card class="card-size atb-ml-5 atb-mr-5 atb-pt-6">
    <template #body>
      <div v-show="exportSystemChooserView">
        <div
          class="atb-flex atb-align-center atb-align-center tools-header-tab"
        >
          <div class="atb-font-bold atb-px-4 atb-py-4">
            {{ $t('system') }}
          </div>
          <Select
            v-model="selectedSystemName"
            :options="systemsArray"
            label="systemName"
            value="id"
            option-class="grid-top-border"
          >
            <template v-slot:option="{ item }">
              {{ item.systemName }}
            </template>
          </Select>
        </div>
        <div class="datasources-table atb-m-4 atb-h-100">
          <div
            class="atb-font-bold atb-px-4 atb-py-4 atb-flex atb-align-center"
          >
            {{ $t('dataSources') }}
          </div>
          <div class="grid-top-border">
            <AgGridVue
              :style="{ height: '350px' }"
              class="ag-theme-eds atb-w-full atb-h-full"
              :column-defs="dsColumnDefs"
              :row-selection="'multiple'"
              :suppress-row-click-selection="true"
              :pagination="false"
              :row-multi-select-with-click="true"
              @grid-ready="onDatasourceGridReady"
              @row-selected="onDataSourceRowSelected"
            />
          </div>
        </div>
      </div>
      <div v-show="exportDataGridView">
        <div class="atb-flex atb-mx-8 tools-header-tab">
          <span>
            {{ $t('exportDataWarning1') }}
          </span>
        </div>
        <div class="datasources-table atb-m-4 atb-h-100">
          <div
            class="atb-font-bold atb-px-4 atb-py-4 atb-flex atb-align-center"
          >
            {{ $t('exportData') }}
          </div>
          <div class="grid-top-border">
            <AgGridVue
              :style="{ height: '350px' }"
              class="ag-theme-eds atb-w-full atb-h-full"
              :column-defs="exportDataColumnDefs"
              :row-selection="'multiple'"
              :suppress-row-click-selection="true"
              :pagination="false"
              :row-multi-select-with-click="true"
              @grid-ready="onExportDataGridReady"
              @row-selected="onExportDataRowSelected"
              :overlay-no-rows-template="t('noExportDataOverlayText')"
            />
          </div>
        </div>
      </div>

      <div class="atb-flex atb-flex-row atb-justify-end atb-align atb-gap-x-8">
        <Button
          motif="primary"
          label="Back"
          :title="$t('back')"
          :styles="{ marginLeft: '10px' }"
          :disabled="disableBackButton"
          @click="onBackButtonClick"
        >
          {{ $t('back') }}
        </Button>
        <Button
          motif="primary"
          label="Next"
          :title="$t('next')"
          :styles="{ marginLeft: '10px' }"
          :disabled="disableNextButton"
          @click="onNextButtonClick"
        >
          {{ $t('next') }}
        </Button>
        <Button
          motif="primary"
          label="$t('export')"
          :title="$t('export')"
          :styles="{ marginLeft: '10px' }"
          :disabled="disableExportButton"
          @click="onExportButtonClick"
        >
          {{ $t('export') }}
        </Button>
      </div>
    </template>
  </Card>
  <AlertPopupModal ref="alertModalRef" />
  <Modal
    :show="showProgressModal"
    container-class="atb-w-1/3"
    body-class="atb-border-none"
    footer-class="atb-flex atb-flex-row atb-justify-end atb-gap-8"
    @onModalClose="showProgressModal = false"
  >
    <template #header> {{ modalHeaderText }} </template>
    <template #body>
      <div class="atb-w-full">
        <div class="progress">
          {{ modalBodyText }}
          <div class="progress__bar"></div>
        </div>
      </div>
    </template>
  </Modal>
</template>

<script lang="ts" setup>
  import { Ref, computed, nextTick, onMounted, reactive, ref } from 'vue';
  import { useClientStore } from '@/app/features/ams/client/client-store';
  import { AgGridEvent, ColumnApi, GridApi } from 'ag-grid-community';
  import { AgGridVue } from 'ag-grid-vue3/lib/AgGridVue';
  import Card from '@atb-ui/eds-vue/src/components/Card.vue';
  import Button from '@atb-ui/eds-vue/src/components/Button.vue';
  import Modal from '@atb-ui/eds-vue/src/components/Modal.vue';
  import Select from '@atb-ui/eds-vue/src/components/Select.vue';
  import { DEPLOY_FILENAME_REGEX } from '@/app/utils/regexpr-const';
  import SystemApi from '@/app/features/ams/systems/systems-service';
  import { useAlertStore } from '@/app/shared/stores/alert-store';
  import { useI18n } from 'vue-i18n';
  import DOMPurify from 'dompurify';
  import { System } from '@/app/shared/types/system.interface';

  const { t } = useI18n();
  const alertStore = useAlertStore();
  const alertModalRef = ref();
  const exportSystemChooserView = ref(true);
  const exportDataGridView = ref(false);
  const dsGridApi = ref(null) as Ref<GridApi | null>;
  const dsColumApi = reactive({}) as Ref<ColumnApi | null>;
  const selectedDataSources: Ref<any[]> = ref([]);
  const exportGridApi = ref(null) as Ref<GridApi | null>;
  const exportColumApi = reactive({}) as Ref<ColumnApi | null>;
  const selectedExportDatas: Ref<any[]> = ref([]);
  const disableBackButton = computed(() => {
    return exportSystemChooserView.value;
  });
  const disableNextButton = computed(() => {
    return !(
      selectedDataSources.value.length > 0 &&
      selectedSystemName.value != '' &&
      !exportDataGridView.value
    );
  });
  const disableExportButton = computed(() => {
    return !exportDataGridView.value;
  });
  const clientStore = useClientStore();
  const client = clientStore.getSelectedClient;
  const clientId = clientStore.selectedClient.id;
  const selectedSystemName = ref();
  const systemsArray = ref<System[]>([]);
  const showProgressModal = ref(false);
  const modalHeaderText = ref('');
  const modalBodyText = ref('');

  const dsColumnDefs = ref([
    {
      field: 'check',
      headerName: '',
      width: 56,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
    },
    {
      field: 'name',
      headerName: t('dataSourceHistory'),
      suppressSizeToFit: true,
      sortable: true,
    },
    {
      field: 'description',
      headerName: t('description'),
      suppressSizeToFit: false,
    },
  ]);

  const exportDataColumnDefs = ref([
    {
      headerName: t('publish'),
      field: 'publish',
      width: 150,
      suppressSizeToFit: true,
      headerCheckboxSelection: true,
      checkboxSelection: true,
      cellRenderer: () => {
        return '';
      },
    },
    {
      field: 'shortName',
      headerName: t('shortName'),
      suppressSizeToFit: false,
    },
    {
      field: 'cmiType',
      headerName: t('type'),
      width: 150,
      suppressSizeToFit: true,
    },
  ]);

  const onDatasourceGridReady = (params: AgGridEvent) => {
    dsGridApi.value = params.api;
    dsGridApi.value.setRowData(client.dataSources);
    dsColumApi.value = params.columnApi;
    dsGridApi.value.sizeColumnsToFit();
  };

  const onDataSourceRowSelected = () => {
    const selectedData = dsGridApi.value?.getSelectedNodes();
    selectedDataSources.value =
      selectedData?.map((node) => node.data.name) || [];
  };

  const onExportDataGridReady = (params: AgGridEvent) => {
    exportGridApi.value = params.api;
    exportColumApi.value = params.columnApi;
  };

  const onExportDataRowSelected = () => {
    const selectedData = exportGridApi.value?.getSelectedNodes();
    selectedExportDatas.value =
      selectedData?.map((node) => node.data.shortName) || [];
  };

  onMounted(async () => {
    const {
      data: { data },
    } = await SystemApi.getSystems(clientId, {});
    systemsArray.value = data;
    nextTick();
  });

  const onBackButtonClick = () => {
    exportSystemChooserView.value = true;
    exportDataGridView.value = false;
  };

  const onNextButtonClick = async () => {
    exportGridApi.value?.setRowData([]);
    const {
      data: { data },
    } = await SystemApi.getProtectedAttributes(
      clientId,
      selectedSystemName.value
    );

    if (data.protectedAttributes && data.protectedAttributes.length > 0) {
      exportGridApi.value?.setRowData(data.protectedAttributes);
      exportGridApi.value?.hideOverlay();
    } else {
      exportGridApi.value?.showNoRowsOverlay();
    }

    exportSystemChooserView.value = false;
    exportDataGridView.value = true;
  };

  const onExportButtonClick = async () => {
    showProgressModal.value = true;
    modalHeaderText.value = t('exportDataTitle');
    modalBodyText.value = t('exportDataBodyText');

    const payLoad = {
      datasources: selectedDataSources.value,
      publishedAttributes: selectedExportDatas.value,
    };
    try {
      const data = await SystemApi.exportData(
        clientId,
        selectedSystemName.value,
        payLoad
      );
      if (data != null && data.request) {
        downloadExportDataFile(data);
      }
    } catch (error: any) {
      alertStore.hideNotification();
      alertStore.showPopupModal('Export!', error?.errors[0]?.message);
    }
    showProgressModal.value = false;
  };

  const downloadExportDataFile = async (response: any) => {
    const disposition = response.request?.getResponseHeader(
      'Content-Disposition'
    );
    let fileName = '';
    const matches = DEPLOY_FILENAME_REGEX.exec(disposition);
    if (matches?.[1]) {
      fileName = matches[1].replace(/['"]/g, '');
    }

    const blob = new Blob([response.data], {
      type: 'application/zip',
    });

    const fileURL = window.URL.createObjectURL(blob);
    const fileLink = document.createElement('a');

    fileLink.href = DOMPurify.sanitize(fileURL);
    fileLink.download = DOMPurify.sanitize(fileName);
    document.body.appendChild(fileLink);
    fileLink.click();
  };
</script>

<style scoped>
  .tools-header-tab {
    align-items: center;
    height: 50px;
  }
  .datasources-table {
    border: 1px solid #d6d6d6;
    border-radius: 6px;
  }
  .grid-top-border {
    border-top: 1px solid #d6d6d6;
  }
</style>
